import React from 'react';
import type { BillingInfo } from '@citrite/sf-api';
import { EcommTaxDetails, TaxIds } from '@sharefile/ecomm-models';
import { Checkbox, type CheckboxChangeEvent, Divider, theme, Typography } from 'antd';
import { t } from '../../translate';
import { addressFieldId } from './addressField';
import { AddressForm } from './AddressForm';
import { useAddressFormContext } from './AddressFormContext';
import { areAddressesSame } from './utils';

const { Text } = Typography;

export interface AccountAndBillingAddressFormProps {
	billingAddress: BillingInfo;
	accountAddress: BillingInfo;
	updateAccountAddress(billingAddress: BillingInfo, retainInfo?: boolean): void;
	updateBillingAddress(billingAddress: BillingInfo, retainInfo?: boolean): void;
	disableAccountAddressCountries?: boolean;
	disableBillingAddressCountries?: boolean;
	taxValidationError: Record<TaxIds, boolean>;
	updateTaxDetails?(taxDetails: EcommTaxDetails): void;
	readOnlyTaxFields?: addressFieldId[];
	addressFormDisabled?: boolean;
}

export const AccountAndBillingAddressForm = (
	props: AccountAndBillingAddressFormProps
) => {
	const { token } = theme.useToken();
	const { accountAddressForm, billingAddressForm } = useAddressFormContext();
	const [isBillingAddressSame, setIsBillingAddressSame] = React.useState<boolean>(
		areAddressesSame(props.accountAddress, props.billingAddress)
	);

	// Show checkbox when billing address country is not disabled,
	// or when it is disabled but both addresses have the same country
	const shouldShowCheckbox =
		!props.disableBillingAddressCountries ||
		(props.disableBillingAddressCountries &&
			props.accountAddress.Country === props.billingAddress.Country);

	/**
	 * toggles the billing address same checkbox
	 * When the checkbox is checked, the billing address
	 * is updated with the account address.
	 * @param e CheckboxChangeEvent
	 */
	const toggleBillingAddressSame = (e: CheckboxChangeEvent) => {
		setIsBillingAddressSame(e.target.checked);
		if (e.target.checked) {
			props.updateBillingAddress(props.accountAddress);
		} else {
			// reset billing address to the empty state with only country and state to preserve tax fields
			props.updateBillingAddress(
				{
					Country: props.accountAddress.Country,
					State: props.accountAddress.State,
				},
				false
			);
			billingAddressForm.resetFields([
				addressFieldId.addressLine1,
				addressFieldId.city,
				addressFieldId.zipCode,
				addressFieldId.postalCode,
			]);
		}
		props.updateTaxDetails?.({});
	};

	/**
	 * updates account address in the parent component
	 * and if the billing address is same, it updates the billing address as well.
	 * @param billingAddress
	 * @param retainInfo
	 */
	const handleAccountAddressChange = (
		billingAddress: BillingInfo,
		retainInfo?: boolean
	) => {
		props.updateAccountAddress(billingAddress);

		if (isBillingAddressSame) {
			props.updateBillingAddress(billingAddress, retainInfo);
		}
	};

	/**
	 * updates billing address in the parent component
	 * @param billingAddress
	 * @param retainInfo
	 */
	const handleBillingAddressChange = (
		billingAddress: BillingInfo,
		retainInfo?: boolean
	) => {
		props.updateBillingAddress(billingAddress, retainInfo);
	};

	return (
		<>
			{!isBillingAddressSame && (
				<Text strong style={{ fontSize: token.fontSizeLG }}>
					{t('billing_address.account_address')}
				</Text>
			)}
			<AddressForm
				addressInfo={props.accountAddress}
				disableCountries={props.disableAccountAddressCountries}
				updateAddressInfo={handleAccountAddressChange}
				form={accountAddressForm}
				updateTaxDetails={props.updateTaxDetails}
				readOnlyTaxFields={props.readOnlyTaxFields}
				showTaxDetails={isBillingAddressSame}
				taxValidationError={props.taxValidationError}
				addressFormDisabled={props.addressFormDisabled}
				testIdPrefix="account-"
			/>

			{shouldShowCheckbox && (
				<Checkbox
					checked={isBillingAddressSame}
					disabled={props.addressFormDisabled}
					onChange={toggleBillingAddressSame}
					style={!isBillingAddressSame ? { marginTop: `-${token.sizeMS}px` } : undefined} // Override flex gap in this one instance to account for form item margins
				>
					{t('billing_address.billing_address_same_message')}
				</Checkbox>
			)}

			{!isBillingAddressSame && (
				<>
					<Divider style={{ margin: 0 }} />
					{/* Other margin/padding makes the spacing look enormously large */}
					<Text strong style={{ fontSize: token.fontSizeLG }}>
						{t('billing_address.title')}
					</Text>
					<AddressForm
						addressInfo={props.billingAddress}
						updateAddressInfo={handleBillingAddressChange}
						form={billingAddressForm}
						disableCountries={props.disableBillingAddressCountries}
						taxValidationError={props.taxValidationError}
						updateTaxDetails={props.updateTaxDetails}
						readOnlyTaxFields={props.readOnlyTaxFields}
						addressFormDisabled={props.addressFormDisabled}
						testIdPrefix="billing-"
					/>
				</>
			)}
		</>
	);
};
